import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CheckCircle, XCircle, MapPin, Clock, Filter, Loader2, RefreshCw, ExternalLink } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Outside Check-in Approvals — compact one-line table.
 *
 * Outside check-ins only have 3 states: pending / approved / rejected
 * (no waived). Same layout as BreakApprovals / LunchApprovals so admins
 * have a consistent muscle memory across the approval pages.
 */

const STATUS_TABS = [
  { id: 'pending', label: 'Pending', tone: 'bg-orange-100 text-orange-700 border-orange-300' },
  { id: 'approved', label: 'Approved', tone: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'rejected', label: 'Rejected', tone: 'bg-red-100 text-red-700 border-red-300' },
  { id: 'all', label: 'All', tone: 'bg-gray-100 text-gray-700 border-gray-300' },
];

const STATUS_BADGE = {
  pending: 'bg-orange-100 text-orange-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400_000).toISOString().slice(0, 10);

export default function OutsideCheckinApprovals() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState('pending');
  const [geoFilter, setGeoFilter] = useState('');
  const [geoOptions, setGeoOptions] = useState([]);
  const [dateRange, setDateRange] = useState({ from: daysAgoISO(6), to: todayISO() });

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/geofences`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setGeoOptions(await r.json());
      } catch {}
    })();
  }, []);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('status', statusTab === 'all' ? 'all' : statusTab);
      if (geoFilter) params.append('geo_fence_id', geoFilter);
      if (dateRange.from) params.append('from_date', dateRange.from);
      if (dateRange.to) params.append('to_date', dateRange.to);
      const r = await fetch(`${API_URL}/api/admin/attendance/pending-approvals?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setRecords(await r.json());
    } finally {
      setLoading(false);
    }
  }, [statusTab, geoFilter, dateRange.from, dateRange.to]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  useAutoRefresh(fetchPending, 30_000);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: records.length };
    for (const r of records) {
      const st = r.outside_checkin_approval?.status;
      if (st && st in c) c[st]++;
    }
    return c;
  }, [records]);

  const handleAction = async (id, action) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/attendance/${id}/approve-outside`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, notes: '' }),
    });
    if (r.ok) {
      fetchPending();
    } else {
      alert('Action failed');
    }
  };

  return (
    <div data-testid="outside-checkin-approvals">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Outside Check-in Approvals</h1>
          <p className="text-xs text-gray-500">{records.length} records · {dateRange.from} → {dateRange.to}{geoFilter && geoOptions.find(g => g.id === geoFilter) ? ` · ${geoOptions.find(g => g.id === geoFilter).name}` : ''}</p>
        </div>
        <button
          onClick={fetchPending}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm"
          data-testid="outside-refresh"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="bg-white border rounded-xl p-3 flex flex-wrap gap-3 items-end mb-3" data-testid="outside-top-filters">
        <div className="flex items-center gap-1 text-gray-500 text-xs"><Filter size={14} /> Filters</div>

        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>Geo</span>
          <select
            className="px-3 py-1.5 border rounded text-sm min-w-[160px]"
            value={geoFilter}
            onChange={(e) => setGeoFilter(e.target.value)}
            data-testid="outside-filter-geo"
          >
            <option value="">All Geo Fences</option>
            {geoOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>

        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>From</span>
          <input
            type="date" value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="px-2 py-1.5 border rounded text-sm w-40"
            data-testid="outside-filter-from"
          />
        </label>
        <label className="text-xs text-gray-600 flex flex-col gap-1">
          <span>To</span>
          <input
            type="date" value={dateRange.to} min={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            className="px-2 py-1.5 border rounded text-sm w-40"
            data-testid="outside-filter-to"
          />
        </label>

        <button
          onClick={() => { setGeoFilter(''); setDateRange({ from: daysAgoISO(6), to: todayISO() }); }}
          className="text-xs text-gray-600 hover:text-gray-900 underline ml-auto"
          data-testid="outside-filter-clear"
        >Reset</button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="outside-status-tabs">
        {STATUS_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setStatusTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              statusTab === t.id ? `${t.tone} font-semibold` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid={`outside-tab-${t.id}`}
          >
            {t.label} <span className="ml-1 opacity-70">({counts[t.id] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="outside-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b text-[11px] uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Staff</th>
                <th className="px-3 py-2 text-left">Geo (Home)</th>
                <th className="px-3 py-2 text-left">Check-in Time</th>
                <th className="px-3 py-2 text-center">Location</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && records.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr><td colSpan="7" className="px-3 py-8 text-center text-gray-500">No outside check-in records match the current filters.</td></tr>
              )}
              {records.map(r => {
                const st = r.outside_checkin_approval?.status || 'pending';
                const ciTime = r.check_in?.time;
                return (
                  <tr key={r.id} className="hover:bg-blue-50" data-testid={`outside-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className="font-medium">{r.staff_name}</span>
                      {r.staff_code && <span className="ml-1 text-[11px] text-gray-400 font-mono">{r.staff_code}</span>}
                      {r.staff_mobile && <span className="ml-1 text-[11px] text-gray-500">· {r.staff_mobile}</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {r.geo_fence_name ? (
                        <span className="inline-flex items-center gap-1"><MapPin size={11} className="text-gray-400" /> {r.geo_fence_name}</span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {ciTime ? (
                        <span className="inline-flex items-center gap-1"><Clock size={11} className="text-gray-400" /> {new Date(ciTime).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {r.check_in?.latitude ? (
                        <a
                          href={`https://maps.google.com/?q=${r.check_in.latitude},${r.check_in.longitude}`}
                          target="_blank" rel="noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs"
                          data-testid={`outside-map-${r.id}`}
                        >
                          View <ExternalLink size={10} />
                        </a>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-0.5 text-[11px] rounded-full capitalize ${STATUS_BADGE[st] || 'bg-gray-100 text-gray-700'}`}>
                        {st}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {st === 'pending' && (
                        <div className="inline-flex gap-1">
                          <button
                            onClick={() => handleAction(r.id, 'approve')}
                            className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded"
                            title="Approve"
                            data-testid={`approve-outside-${r.id}`}
                          ><CheckCircle size={14} /></button>
                          <button
                            onClick={() => handleAction(r.id, 'reject')}
                            className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded"
                            title="Reject"
                            data-testid={`reject-outside-${r.id}`}
                          ><XCircle size={14} /></button>
                        </div>
                      )}
                      {st !== 'pending' && r.outside_checkin_approval?.notes && (
                        <span className="text-[11px] text-gray-500 italic truncate max-w-[160px] inline-block" title={r.outside_checkin_approval.notes}>
                          {r.outside_checkin_approval.notes}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
